import React, { useState, useEffect } from 'react';
import {
    Typography,
    Card,
    Button,
    Space,
    Form,
    Input,
    DatePicker,
    Switch,
    InputNumber,
    message,
    Breadcrumb,
    Spin,
    Tag,
    Upload,
    Table,
    Modal,
    Popconfirm,
    Row,
    Col,
    Divider,
    Tabs,
    Alert,
    Checkbox,
    Radio,
    Tooltip
} from 'antd';
import * as XLSX from 'xlsx';
import {
    ArrowLeftOutlined,
    LineChartOutlined,
    SaveOutlined,
    PlusOutlined,
    FileExcelOutlined,
    DeleteOutlined,
    EditOutlined,
    InfoCircleOutlined,
    CheckCircleFilled,
    EyeOutlined,
    StopOutlined,
    SendOutlined,
    BellOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import dayjs from 'dayjs';
import { TrainingType } from '../types/training';
import {
    adminGetTrainingById,
    adminUpdateTraining,
    adminBulkUploadQuestions,
    adminToggleTrainingStatus,
    adminAssignParticipants,
    adminClearSubmissionsByTraining,
    adminDeleteTraining,
    adminSummarizeChanges,
    adminSendProgramUpdateNotification
} from '../services/adminService';
import { publishMoodleProgram, deleteMoodleQuiz } from '../services/moodleService';
import { parseModuleId } from '../utils/moduleUtils';
import { detectCommonChanges } from '../utils/changeDetection';

const { Title, Text } = Typography;
const { TextArea } = Input;

const EditObjectiveProgram: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [modules, setModules] = useState<{ name: string, estimatedTime: number }[]>([]);
    const [newModule, setNewModule] = useState('');
    const [fileList, setFileList] = useState<any[]>([]);
    const [isAddModuleModalVisible, setIsAddModuleModalVisible] = useState(false);
    const [pendingUploads, setPendingUploads] = useState<{ module: string, questions: any[] }[]>([]);
    const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);

    const [isActive, setIsActive] = useState(true);
    const [isPublished, setIsPublished] = useState(true);
    const [notifyParticipant, setNotifyParticipant] = useState(true);
    const [initialValues, setInitialValues] = useState<any>(null); // State to hold initial values for form
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');

    useEffect(() => {
        if (id) {
            fetchTrainingData(parseInt(id));
        }
    }, [id]);

    const fetchTrainingData = async (trainingId: number) => {
        try {
            setFetching(true);
            const data = await adminGetTrainingById(trainingId);
            const isEvergreen = !data.startDate || !data.endDate;
            const formValues = {
                trainingId: data.trainingId,
                trainingName: data.trainingName,
                active: data.active,
                dateRange: isEvergreen ? null : [
                    data.startDate ? dayjs(data.startDate.split('T')[0]) : null,
                    data.endDate ? dayjs(data.endDate.split('T')[0]) : null
                ],
                isEvergreen: isEvergreen,
                passingPercentage: data.passingPercentage,
                estimatedEffortHours: data.estimatedEffortHours,
                trainingLink: data.trainingLink,
                audienceEmails: data.audienceEmails,
                stakeholderEmails: data.stakeholderEmails,
                assessments: data.assessments,
                notificationVersion: (data.notificationVersion === null || data.notificationVersion === undefined || data.notificationVersion === '') ? '1.0' : String(data.notificationVersion),
                trainingType: data.trainingType || TrainingType.OPEN
            };
            form.setFieldsValue(formValues);
            setInitialValues(formValues);
            const isDraft = data.ispublished === false;
            setIsPublished(!isDraft);
            setIsActive(!!data.active || isDraft);
            const existingMods = data.assessments ? data.assessments.split('|').map((s: string) => s.trim()).filter(Boolean) : [];
            const mappedModules = existingMods.map((m: string) => {
                const { name, estimatedTime } = parseModuleId(m);
                return { name, estimatedTime };
            });
            setModules(mappedModules);

        } catch (error) {
            message.error('Failed to load training data');
            navigate('/admin');
        } finally {
            setFetching(false);
        }
    };

    const handleAddModule = async () => {
        const trimmed = newModule.trim();
        if (!trimmed) {
            message.error('Module name is required');
            return;
        }

        if (modules.some(m => m.name.toLowerCase() === trimmed.toLowerCase())) {
            message.warning('Module name already exists!');
            return;
        }

        if (fileList.length === 0) {
            message.error('Please upload questions Excel file for the new module');
            return;
        }

        try {
            setLoading(true);
            const file = fileList[0].originFileObj;
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const questionData = jsonData
                .map((row: any) => ({
                    Question: String(row.Question || row.question || '').trim(),
                    CodeSnippet: String(row['Code Snippet'] || row.CodeSnippet || row['Code snippet'] || '').trim(),
                    OptionA: String(row.OptionA || row.optionA || '').trim(),
                    OptionB: String(row.OptionB || row.optionB || '').trim(),
                    OptionC: String(row.OptionC || row.optionC || '').trim(),
                    OptionD: String(row.OptionD || row.optionD || '').trim(),
                    Answer: String(row.Answer || row.answer || '').trim().toUpperCase()
                }))
                .filter((q: any) => q.Question.length > 0 && q.Answer.length > 0);

            if (questionData.length === 0) {
                throw new Error('No valid questions found in Excel');
            }

            // Queue upload (do not call API yet)
            setPendingUploads([...pendingUploads, { module: trimmed, questions: questionData }]);

            // Update modules list
            const updated = [...modules, { name: trimmed, estimatedTime: 30 }];
            setModules(updated);
            setNewModule('');
            setFileList([]);
            setIsAddModuleModalVisible(false);
            form.setFieldsValue({ assessments: updated.map(m => `${m.name} #T${m.estimatedTime}`).join('|') });
            message.success(`Module "${trimmed}" added and questions uploaded.`);
        } catch (error: any) {
            message.error(`Failed to add module: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveModule = async (modName: string) => {
        if (modules.length <= 1) {
            message.error('At least one module must remain in the program.');
            return;
        }

        // Check if it's a pending upload
        const isPendingUpload = pendingUploads.some(u => u.module === modName);
        if (isPendingUpload) {
            setPendingUploads(pendingUploads.filter(u => u.module !== modName));
        } else {
            setPendingDeletions([...pendingDeletions, modName]);
        }

        const updated = modules.filter(m => m.name !== modName);
        setModules(updated);
        form.setFieldsValue({ assessments: updated.map(m => `${m.name} #T${m.estimatedTime}`).join('|') });
        message.success(`Module "${modName}" deleted successfully.`);

    };

    const handleFinish = async (values: any) => {
        try {
            const objNames = modules.map(m => m.name.trim().toLowerCase());
            const dupObjMod = objNames.find((n, i) => objNames.indexOf(n) !== i);
            if (dupObjMod) {
                message.error(`Module names must be unique. Duplicate: "${modules.find(m => m.name.trim().toLowerCase() === dupObjMod)?.name}"`);
                return;
            }
            setLoading(true);

            // 1. Process Deletions - delete each removed module's Moodle quiz, so
            // completion and progress stop counting it.
            if (pendingDeletions.length > 0) {
                const courseId = initialValues?.id;
                await Promise.all(pendingDeletions.map(modName => deleteMoodleQuiz(courseId, modName)));
                setPendingDeletions([]);
            }

            // 2. Determine added emails for assignment
            const emailsAdded: string[] = [];
            const isTypeOpen = values.trainingType === TrainingType.OPEN;
            if (!isTypeOpen && values.audienceEmails) {
                // If it's a new program or email list changed, check for new assignments
                // (Simplifying for this refactor to just re-assign or append)
                const emails = values.audienceEmails.split(/[\n,;]+/).map((e: string) => e.trim()).filter(Boolean);
                if (emails.length > 0) {
                    emailsAdded.push(...emails);
                }
            }

            const cleanEmails = (str: string) => str ? str.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean).join(';') : '';
            const finalAssessmentsStr = modules.map(m => `${m.name.trim()} #T${m.estimatedTime || 30}`).join(' | ');

            const newStartDate = values.isEvergreen ? '' : (values.dateRange?.[0] ? values.dateRange[0].format('YYYY-MM-DD') : '');
            const newEndDate = values.isEvergreen ? '' : (values.dateRange?.[1] ? values.dateRange[1].format('YYYY-MM-DD') : '');

            // Capture field-level changes before save (form values vs loaded data)
            const changeDescriptions = detectCommonChanges(initialValues, {
                trainingName: values.trainingName,
                startDate: newStartDate,
                endDate: newEndDate,
                isEvergreen: values.isEvergreen,
                passingPercentage: values.passingPercentage,
                estimatedEffortHours: values.estimatedEffortHours,
                trainingType: values.trainingType,
                trainingLink: values.trainingLink || ''
            });
            const oldModules = (initialValues?.assessments || '').split('|').map((s: string) => s.replace(/#[A-Z]\d+/gi, '').trim()).filter(Boolean);
            const newModuleNames = modules.map(m => m.name.trim());
            const addedMods = newModuleNames.filter(m => !oldModules.includes(m));
            const removedMods = oldModules.filter((m: string) => !newModuleNames.includes(m));
            const nameList = (names: string[]) => names.length <= 3 ? names.join(', ') : `${names.length} modules`;
            if (addedMods.length > 0) changeDescriptions.push({ field: 'Modules Added', value: nameList(addedMods) });
            if (removedMods.length > 0) changeDescriptions.push({ field: 'Modules Removed', value: nameList(removedMods) });

            const finalTrainingData: any = {
                trainingId: values.trainingId,
                trainingName: values.trainingName,
                active: values.active,
                startDate: newStartDate,
                endDate: newEndDate,
                passingPercentage: values.passingPercentage,
                estimatedEffortHours: values.estimatedEffortHours,
                trainingLink: values.trainingLink || '',
                audienceEmails: values.trainingType === TrainingType.OPEN ? '' : cleanEmails(values.audienceEmails),
                stakeholderEmails: values.trainingType === TrainingType.OPEN ? '' : cleanEmails(values.stakeholderEmails),
                assessments: finalAssessmentsStr,
                notificationVersion: values.notificationVersion,
                trainingType: values.trainingType,
            };

            await adminUpdateTraining(parseInt(id!), finalTrainingData);

            if (pendingUploads.length > 0) {
                for (const upload of pendingUploads) {
                    await adminBulkUploadQuestions(values.trainingId, upload.module, upload.questions);
                }
            }

            if (emailsAdded.length > 0) {
                await adminAssignParticipants(values.trainingId, modules.map(m => m.name), emailsAdded);
            }

            // All operations done — now build AI summary and send notification
            if (notifyParticipant && changeDescriptions.length > 0) {
                const changeSummary = await adminSummarizeChanges(changeDescriptions);
                if (changeSummary) await adminSendProgramUpdateNotification(initialValues?.id, changeSummary, user?.name || user?.username || '');
            }

            message.success('Training program updated successfully');
        } catch (error: any) {
            message.error(error.message || 'Failed to update training program');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseProgram = () => {
        const trainingId = form.getFieldValue('trainingId');
        const trainingName = form.getFieldValue('trainingName');

        Modal.confirm({
            title: 'Are you sure you want to close this program?',
            icon: <StopOutlined style={{ color: '#ff4d4f' }} />,
            content: (
                <div>
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff1f0', borderRadius: '8px', border: '1px solid #ffa39e' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{trainingName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>ID: {trainingId}</div>
                    </div>
                    <p>Once a program is closed (Inactive), it:</p>
                    <ul style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        <li>Cannot be reactivated again</li>
                        <li>Cannot be edited further</li>
                        <li>Participants will see it as "Closed" on their dashboard</li>
                    </ul>
                    <p>Do you wish to proceed?</p>
                </div>
            ),
            okText: 'Yes, Close Program',
            okType: 'danger',
            cancelText: 'No, Keep Active',
            onOk: async () => {
                try {
                    setLoading(true);
                    await adminToggleTrainingStatus(parseInt(id!), true);
                    await adminClearSubmissionsByTraining(trainingId);
                    message.success('Program closed successfully');
                    await fetchTrainingData(parseInt(id!));
                    setIsActive(false); // Override stale Moodle cache if fetchTrainingData returns stale active=true
                } catch (error) {
                    message.error('Failed to close program');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handlePublish = async () => {
        try {
            setLoading(true);
            await publishMoodleProgram(parseInt(id!));
            setIsPublished(true);
            message.success('Program published! Participants can now access it.');
        } catch { message.error('Failed to publish program'); }
        finally { setLoading(false); }
    };

    const handleDeleteTraining = async () => {
        if (!initialValues) return;
        if (deleteConfirmationText.trim() !== initialValues.trainingName.trim()) {
            message.error('Program name does not match');
            return;
        }
        try {
            setLoading(true);
            await adminDeleteTraining(parseInt(id!), initialValues.trainingId);
            message.success('Program deleted successfully');
            setIsDeleteModalVisible(false);
            navigate('/admin');
        } catch (error) {
            message.error('Failed to delete program');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div style={{ padding: '100px', textAlign: 'center' }}>
                <Spin size="large" tip="Loading training details..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: '16px' }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin')}>
                        Back to Admin
                    </Button>
                    <Button
                        icon={<LineChartOutlined />}
                        type="primary"
                        ghost
                        style={{ borderColor: '#fa541c', color: '#fa541c' }}
                        onClick={() => navigate(`/training/${encodeURIComponent(form.getFieldValue('trainingId'))}?mode=analyze`)}
                    >
                        Analyze
                    </Button>
                </div>
                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                            width: 64, height: 64, flexShrink: 0, borderRadius: '16px',
                            backgroundColor: '#fff7e6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {isActive ? (
                                <EditOutlined style={{ fontSize: '32px', color: '#fa541c' }} />
                            ) : (
                                <EyeOutlined style={{ fontSize: '32px', color: '#fa541c' }} />
                            )}
                        </div>
                        <div style={{ flex: 1 }}>
                            <Space align="center">
                                <Title level={2} style={{ margin: 0 }}>{isActive ? 'Edit Training Program' : 'View Training Program Details'}</Title>
                                {!isActive && <Tag color="error" style={{ marginLeft: '8px' }}>PROGRAM CLOSED</Tag>}
                                {isActive && !isPublished && <Tag color="orange" style={{ marginLeft: '8px' }}>DRAFT</Tag>}
                            </Space>
                            <br />
                            <Text type="secondary">{isActive ? 'Modify program details, manage modules, and update assessments.' : 'Review program configuration and assessment modules.'}</Text>
                        </div>
                        {isActive && !isPublished && (
                            <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={handlePublish} size="large">
                                Publish Program
                            </Button>
                        )}
                    </div>
                </Card>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{ notificationVersion: '1.0', trainingType: TrainingType.OPEN, ...initialValues }} // Merged initialValues
            >
                <Card title="1. Program Information" style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item name="trainingId" label="Training ID">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="trainingName" label="Program Name" rules={[{ required: true }]}>
                        <Input 
                            placeholder="Enter full program name" 
                            size="large" 
                            disabled={!isActive} 
                            maxLength={250}
                            showCount
                        />
                    </Form.Item>

                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.trainingType !== currentValues.trainingType || prevValues.isEvergreen !== currentValues.isEvergreen}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const trainingType = getFieldValue('trainingType');
                                    const isEvergreen = getFieldValue('isEvergreen');

                                    // Auto-uncheck evergreen if they switch away from Open
                                    if (trainingType !== TrainingType.OPEN && isEvergreen) {
                                        setTimeout(() => setFieldsValue({ isEvergreen: false }), 0);
                                    }

                                    return (
                                        <>
                                            {trainingType === TrainingType.OPEN && (
                                                <Form.Item name="isEvergreen" valuePropName="checked" style={{ marginBottom: 16 }}>
                                                    <Switch checkedChildren="Evergreen (No Fixed Dates)" unCheckedChildren="Fixed Dates" disabled={!isActive} />
                                                </Form.Item>
                                            )}

                                            {!isEvergreen || trainingType !== TrainingType.OPEN ? (
                                                <Form.Item
                                                    label="Training Period"
                                                    name="dateRange"
                                                    rules={[{ required: true, message: 'Please select a training period' }]}
                                                >
                                                    <DatePicker.RangePicker style={{ width: '100%' }} size="large" disabled={!isActive} />
                                                </Form.Item>
                                            ) : (
                                                <Alert
                                                    message="Evergreen Program"
                                                    description="This training has no fixed dates and is available immediately."
                                                    type="info"
                                                    showIcon
                                                    style={{ marginBottom: 24 }}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Passing %" name="passingPercentage" rules={[{ required: true }]}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }} size="large" disabled={!isActive} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Assessment Duration (Hours)" name="estimatedEffortHours">
                                <InputNumber min={0} style={{ width: '100%' }} size="large" disabled={!isActive} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="Resource / Documentation Link" name="trainingLink" rules={[{ required: true }]}>
                        <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="https://..." disabled={!isActive} style={{ borderRadius: '8px' }} />
                    </Form.Item>

                    <Divider />
                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.trainingType !== currentValues.trainingType}>
                        {({ getFieldValue }) => {
                            const type = getFieldValue('trainingType');
                            if (type === 'Open') return null;
                            return (
                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Participants (Emails, semicolon separated)" name="audienceEmails" rules={[{ required: true }]}>
                                            <TextArea rows={4} placeholder="user1@company.com; user2@company.com" style={{ borderRadius: '8px' }} disabled={!isActive} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Stakeholders (Emails, semicolon separated)" name="stakeholderEmails" rules={[{ required: true, message: 'Stakeholder emails are mandatory' }]}>
                                            <TextArea rows={4} placeholder="manager@company.com; director@company.com" style={{ borderRadius: '8px' }} disabled={!isActive} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            );
                        }}
                    </Form.Item>
                </Card>

                <Card
                    title="2. Modules & Assessments"
                    style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                    extra={
                        isActive && (
                            <Button type="dashed" onClick={() => setIsAddModuleModalVisible(true)} icon={<PlusOutlined />} style={{ borderColor: '#fa541c', color: '#fa541c' }}>
                                Add Module
                            </Button>
                        )
                    }
                >
                    <Table
                        dataSource={modules}
                        columns={[
                            { title: 'Module Name', dataIndex: 'name', key: 'name' },
                            {
                                title: 'Time (Mins)',
                                dataIndex: 'estimatedTime',
                                key: 'estimatedTime',
                                width: 120,
                                render: (time: number, record: any) => (
                                    <InputNumber
                                        min={1}
                                        max={300}
                                        value={time}
                                        onChange={(val) => {
                                            const updated = modules.map(m => m.name === record.name ? { ...m, estimatedTime: val || 30 } : m);
                                            setModules(updated);
                                        }}
                                        disabled={!isActive}
                                    />
                                )
                            },
                            {
                                title: 'Actions',
                                key: 'actions',
                                width: 120,
                                render: (_, record) => (
                                    <Popconfirm
                                        title="Delete this module?"
                                        description="This will delete the module and all its questions permanently."
                                        onConfirm={() => handleRemoveModule(record.name)}
                                        okText="Yes"
                                        cancelText="No"
                                        disabled={modules.length <= 1 || !isActive}
                                    >
                                        <Button type="text" danger icon={<DeleteOutlined />} disabled={modules.length <= 1 || !isActive}>Delete</Button>
                                    </Popconfirm>
                                )
                            }
                        ]}
                        pagination={false}
                        rowKey="name"
                        bordered
                        size="small"
                        locale={{ emptyText: 'No modules configured.' }}
                    />
                    <Form.Item name="assessments" noStyle hidden>
                        <Input />
                    </Form.Item>
                </Card>

                <Card title="3. Program Accessibility" style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <Form.Item label="Training Accessibility Type" name="trainingType" rules={[{ required: true }]}>
                        <Radio.Group disabled={!isActive}>
                            <Space direction="vertical">
                                <Radio value={TrainingType.OPEN}>
                                    <Text strong>Open for All</Text>
                                    <br />
                                    <Text type="secondary">No nominations required. Anyone can join and take the assessment anytime.</Text>
                                </Radio>
                                <Radio value={TrainingType.REQUESTABLE}>
                                    <Text strong>Nominated + Request to Join</Text>
                                    <br />
                                    <Text type="secondary">Specific people are nominated. Others can request to join before the start date.</Text>
                                </Radio>
                                <Radio value={TrainingType.PRIVATE}>
                                    <Text strong>Private (Invite Only)</Text>
                                    <br />
                                    <Text type="secondary">Only people explicitly nominated can see and access this program.</Text>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                </Card>

                <div style={{ textAlign: 'right', marginTop: '32px', marginBottom: '50px' }}>
                    <Space size="large">
                        {(!isActive || !isPublished) && (
                            <Button
                                danger
                                size="large"
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                    setDeleteConfirmationText('');
                                    setIsDeleteModalVisible(true);
                                }}
                                style={{ minWidth: '160px' }}
                            >
                                Delete Program
                            </Button>
                        )}
                        <Button onClick={() => navigate('/admin')} size="large" style={{ minWidth: '160px' }}>
                            {isActive ? 'Cancel' : 'Back to Dashboard'}
                        </Button>
                        {isActive && (
                            <>
                                {isPublished && (
                                    <Button
                                        danger
                                        size="large"
                                        icon={<StopOutlined />}
                                        onClick={handleCloseProgram}
                                        style={{ minWidth: '160px' }}
                                    >
                                        Close Program
                                    </Button>
                                )}
                                <Tooltip title={notifyParticipant ? 'Participants will be notified by email on save' : 'Participants will NOT be notified on save'}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: notifyParticipant ? '#f0f5ff' : '#fafafa', borderRadius: 6, border: `1px solid ${notifyParticipant ? '#adc6ff' : '#d9d9d9'}`, cursor: 'default' }}>
                                        <BellOutlined style={{ color: notifyParticipant ? '#1890ff' : '#bfbfbf', fontSize: 14 }} />
                                        <Typography.Text style={{ fontSize: 13, color: notifyParticipant ? '#1890ff' : '#8c8c8c', whiteSpace: 'nowrap' }}>Notify Participants</Typography.Text>
                                        <Switch size="small" checked={notifyParticipant} onChange={setNotifyParticipant} />
                                    </div>
                                </Tooltip>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<SaveOutlined />}
                                    htmlType="submit"
                                    loading={loading}
                                    style={{ backgroundColor: '#fa541c', borderColor: '#fa541c', minWidth: '160px' }}
                                >
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </Space>
                </div>
            </Form>

            <Modal
                title={<span style={{ color: 'red' }}>Delete Program: {initialValues?.trainingName} ({initialValues?.trainingId})</span>}
                open={isDeleteModalVisible}
                onCancel={() => {
                    setIsDeleteModalVisible(false);
                    setDeleteConfirmationText('');
                }}
                onOk={handleDeleteTraining}
                okText="Permanently Delete"
                okButtonProps={{ 
                    danger: true, 
                    disabled: deleteConfirmationText.trim() !== initialValues?.trainingName?.trim(), 
                    loading: loading 
                }}
                centered
            >
                <Alert
                    message="Warning: This action cannot be undone."
                    description="This will permanently delete the training program and ALL associated data from Moodle."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Text style={{ display: 'block', marginBottom: 8 }}>
                    Please type <strong>{initialValues?.trainingName}</strong> to confirm:
                </Text>
                <Input
                    value={deleteConfirmationText}
                    onChange={(e) => setDeleteConfirmationText(e.target.value)}
                    placeholder="Type program name exactly"
                    maxLength={250}
                />
            </Modal>

            <Modal
                title="Add New Module"
                open={isAddModuleModalVisible}
                onCancel={() => setIsAddModuleModalVisible(false)}
                onOk={handleAddModule}
                confirmLoading={loading}
                okText="Add Module & Upload"
                okButtonProps={{
                    disabled: !newModule || fileList.length === 0,
                    style: { backgroundColor: '#fa541c', borderColor: '#fa541c', color: '#fff' }
                }}
                maskClosable={false}
                destroyOnClose
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '10px 0' }}>
                    <div>
                        <Typography.Text strong>Module Name</Typography.Text>
                        <Input
                            placeholder="e.g. Introduction to React"
                            value={newModule}
                            onChange={e => setNewModule(e.target.value)}
                            style={{ marginTop: '8px', borderRadius: '6px' }}
                        />
                    </div>
                    <div>
                        <Typography.Text strong>Questions File (Excel)</Typography.Text>
                        <div style={{ marginTop: '8px' }}>
                            <Upload
                                beforeUpload={() => false}
                                fileList={fileList}
                                onChange={({ fileList }) => setFileList(fileList)}
                                maxCount={1}
                                accept=".xlsx,.xls"
                            >
                                <Button icon={<FileExcelOutlined />} block>Select Questions File</Button>
                            </Upload>
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EditObjectiveProgram;

